import { useState } from 'react';
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleDot,
  Copy,
  ListChecks,
  Send,
} from 'lucide-react';
import type { DocumentRequest, Requirement } from '../hooks/useChildSupport';
import { Button, Card, Tag } from './ui';

interface Props {
  requirements: Requirement[];
  requests: DocumentRequest[];
  blockingCount: number;
}

/**
 * The readiness spine: what do we have, what did the data find, what's missing —
 * and for anything missing, the request already written and routed to the right
 * source. A missing input is a discovery task, not a blank form field.
 */

const GROUPS: {
  status: Requirement['status'];
  title: string;
  icon: React.ReactNode;
  accent: string;
}[] = [
  {
    status: 'missing',
    title: 'Needed',
    icon: <CircleAlert size={16} className="text-danger" />,
    accent: 'border-danger-accent',
  },
  {
    status: 'candidate',
    title: 'Found — your decision',
    icon: <CircleDot size={16} className="text-clay-600" />,
    accent: 'border-clay-300',
  },
  {
    status: 'incomplete',
    title: 'Incomplete',
    icon: <CircleDot size={16} className="text-warn" />,
    accent: 'border-warn-accent',
  },
];

function PartyChip({ party }: { party: string | null }) {
  if (!party) return null;
  return (
    <Tag tone={party === 'client' ? 'client' : 'opposing'} size="sm">
      {party}
    </Tag>
  );
}

function ItemRow({ item }: { item: Requirement }) {
  return (
    <li className="flex gap-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-ink">{item.label}</span>
          <PartyChip party={item.party} />
          {item.required && item.status === 'missing' && (
            <Tag tone="danger" size="sm">required</Tag>
          )}
        </div>
        {item.detail && <p className="mt-1 text-xs text-ink-soft">{item.detail}</p>}
        {item.statute && <p className="mt-1 text-2xs text-ink-faint">{item.statute}</p>}
      </div>
    </li>
  );
}

function RequestCard({ request }: { request: DocumentRequest }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(request.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="overflow-hidden rounded-lg border border-clay-200 bg-clay-50">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Send size={16} className="shrink-0 text-clay-700" />
          <span className="truncate text-sm font-semibold text-clay-900">
            {request.recipient_label}
          </span>
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-clay-700 px-1 text-xs font-bold leading-none text-white">
            {request.lines.length}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            icon={open ? ChevronDown : ChevronRight}
            onClick={() => setOpen((v) => !v)}
            className="text-clay-700 hover:text-clay-900"
          >
            Preview
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            icon={copied ? Check : Copy}
            onClick={copy}
          >
            {copied ? 'Copied' : 'Copy request'}
          </Button>
        </div>
      </div>
      {open && (
        <pre className="whitespace-pre-wrap border-t border-border-subtle px-4 pb-4 pt-1 font-sans text-xs leading-relaxed text-ink-soft">
          {request.text}
        </pre>
      )}
    </div>
  );
}

export default function RequirementsSpine({ requirements, requests, blockingCount }: Props) {
  const byStatus = (status: Requirement['status']) =>
    requirements.filter((r) => r.status === status);
  const onFile = byStatus('established');
  const open = requirements.length - onFile.length;

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle px-6 py-4">
        <div className="flex items-center gap-2">
          <ListChecks size={16} className="text-green-700" />
          <h3 className="t-card text-green-900">Case readiness</h3>
        </div>
        <div className="text-xs text-ink-soft">
          {open === 0 ? (
            <span className="flex items-center gap-2 font-medium text-good">
              <CheckCircle2 size={16} /> Every input is on file
            </span>
          ) : (
            <>
              <span className="font-semibold text-ink">{open}</span> of {requirements.length}{' '}
              items open
              {blockingCount > 0 && (
                <span className="text-danger"> · generation blocked until resolved</span>
              )}
            </>
          )}
        </div>
      </div>

      <div className="space-y-5 p-6">
        {GROUPS.map(({ status, title, icon, accent }) => {
          const items = byStatus(status);
          if (!items.length) return null;
          return (
            <div key={status} className={`rounded-lg border ${accent} px-4 py-1`}>
              <div className="flex items-center gap-2 pt-2">
                {icon}
                <span className="t-eyebrow">{title}</span>
                <span className="text-xs text-ink-faint">({items.length})</span>
              </div>
              <ul className="divide-y divide-border-subtle">
                {items.map((item) => (
                  <ItemRow key={item.key} item={item} />
                ))}
              </ul>
            </div>
          );
        })}

        {requests.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs text-ink-soft">
              Requests for the missing items, already routed — your client's documents are
              asked of your client, the opposing party's of opposing counsel.
            </p>
            {requests.map((r) => (
              <RequestCard key={r.recipient} request={r} />
            ))}
          </div>
        )}

        {onFile.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="mr-1 text-xs text-ink-faint">On file:</span>
            {onFile.map((item) => (
              <Tag key={item.key} tone="good" icon={Check} title={item.detail}>
                {item.label}
              </Tag>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
